/**
 * Modal — center-screen modal with fade + scale entrance.
 *
 * Use for confirmation prompts, image previews, anything that's not a
 * full bottom sheet. Scales from 0.95 → 1 on open, 1 → 0.95 on close,
 * matching the iOS / Apple Maps modal personality.
 */
import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { DURATION, EASE } from "@/shared/motion/tokens";

export default function Modal({ open, onClose, children, testid, className = "", ariaLabel }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="modal-backdrop"
          className="fixed inset-0 z-50 bg-black/55 backdrop-blur-md flex items-center justify-center p-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DURATION.normal, ease: EASE.out }}
          onClick={onClose}
          data-testid={testid ? `${testid}-backdrop` : undefined}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: DURATION.normal, ease: EASE.out }}
            className={`max-w-md w-full ${className}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            data-testid={testid}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
